import { useMemo } from "react";
import { useApi } from "../../hooks";
import { fmtOutage, fmtUptime, uptimeHours, uptimeStatus, type Uptime, type UptimeSlot } from "../../web-analytics";

type Props = {
  clientId: number;
  siteId: number;
  timeZone: string;
  /** The settings dialog's version: status, 30-day uptime and the strip, no incidents. */
  compact?: boolean;
};

const SLOT_TEXT = { up: "online", down: "down", blocked: "check blocked", none: "not checked" } as const;

/**
 * The site's hourly uptime checks (utils/uptime.js), ported from Sonar's
 * SiteUptimePanel: the current state, 30-day uptime, a strip of the last 48
 * hours and the recent incidents.
 */
export default function UptimePanel({ clientId, siteId, timeZone, compact = false }: Props) {
  const uptime = useApi<Uptime>(`/api/web/${clientId}/uptime?site=${siteId}`);
  const when = useMemo(
    () => new Intl.DateTimeFormat("en-US", { timeZone, month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" }),
    [timeZone],
  );
  const hour = useMemo(() => new Intl.DateTimeFormat("en-US", { timeZone, weekday: "short", hour: "numeric" }), [timeZone]);
  const u = uptime.data;

  const body = uptime.error ? (
    <div className="wa-bd-empty wa-bd-error" role="alert">{uptime.error}</div>
  ) : !u ? (
    <div className="wa-bd-empty">Loading uptime…</div>
  ) : (
    <Body u={u} when={when} hour={hour} compact={compact} />
  );

  if (compact) return <div className="wa-up wa-up-compact" aria-label="Uptime so far">{body}</div>;
  return (
    <section className="panel wa-up" aria-labelledby="wa-up-heading">
      <div className="panel-heading">
        <div className="wa-bd-title">
          <h2 id="wa-up-heading">Uptime</h2>
          <p className="panel-meta">{u?.url ? <>Hourly checks of <span className="wa-up-url">{u.url}</span></> : "Hourly checks of the site's home page"}</p>
        </div>
        {u && <Badge u={u} />}
      </div>
      {body}
    </section>
  );
}

function Badge({ u }: { u: Uptime }) {
  const s = uptimeStatus(u);
  return <span className={`status-badge ${s.tone}`}>{s.label}</span>;
}

function Body({ u, when, hour, compact }: { u: Uptime; when: Intl.DateTimeFormat; hour: Intl.DateTimeFormat; compact: boolean }) {
  const slots = uptimeHours(u.checks);
  const latest = u.checks[0];
  if (compact && !latest) return <p className="wa-hint">No checks yet.</p>;
  if (!u.monitor && !latest)
    return <p className="wa-up-off">Uptime checks are off for this site. They can be turned on in site settings.</p>;
  return (
    <div className="wa-up-body">
      <div className="wa-up-stats">
        {compact && <div className="wa-up-stat"><Badge u={u} /><span className="wa-attr-caption">Now</span></div>}
        <div className="wa-up-stat">
          <strong>{fmtUptime(u.uptime_30d)}</strong>
          <span className="wa-attr-caption">Uptime, 30 days</span>
        </div>
        {!compact && (
          <div className="wa-up-stat">
            <strong>{latest?.ms == null ? "—" : `${latest.ms} ms`}</strong>
            <span className="wa-attr-caption">Latest response</span>
          </div>
        )}
        <div className="wa-up-stat">
          <strong className="wa-up-when">{latest ? when.format(new Date(latest.checked_at)) : "First check pending"}</strong>
          <span className="wa-attr-caption">Last checked</span>
        </div>
      </div>
      <Strip slots={slots} hour={hour} />
      {!compact && (
        <p className="wa-hint wa-up-note">
          One check an hour, retried once on failure, so a short outage between checks can be missed. Gray hours were not checked or the site refused the check; they are left out of uptime.
          {!u.monitor && " Checks are off for this site now."}
        </p>
      )}
      {!compact && latest?.error && <p className="wa-up-error">Last check: {latest.error}</p>}
      {!compact && <Incidents u={u} when={when} />}
    </div>
  );
}

function Strip({ slots, hour }: { slots: UptimeSlot[]; hour: Intl.DateTimeFormat }) {
  const down = slots.filter((s) => s.kind === "down").length;
  const up = slots.filter((s) => s.kind === "up").length;
  return (
    <div className="wa-up-strip-wrap">
      <div
        className="wa-up-strip"
        role="img"
        aria-label={`Last 48 hours: ${up} hours online, ${down} down, ${48 - up - down} not checked or blocked`}
      >
        {slots.map((s) => (
          <span
            key={s.at}
            className={`wa-up-slot ${s.kind}`}
            title={`${hour.format(new Date(s.at))}: ${SLOT_TEXT[s.kind]}${s.check?.error ? ` (${s.check.error})` : ""}`}
          />
        ))}
      </div>
      <div className="wa-up-axis" aria-hidden="true"><span>48 hours ago</span><span>Now</span></div>
    </div>
  );
}

function Incidents({ u, when }: { u: Uptime; when: Intl.DateTimeFormat }) {
  return (
    <div className="wa-up-incidents">
      <h3>Recent incidents</h3>
      {u.incidents.length === 0 ? (
        <p className="wa-hint">No downtime recorded.</p>
      ) : (
        <ul>
          {u.incidents.map((i) => {
            const start = new Date(i.started_at);
            const end = i.resolved_at ? new Date(i.resolved_at) : null;
            return (
              <li key={i.id}>
                <span className={`status-badge ${end ? "" : "danger"}`}>{end ? "Resolved" : "Ongoing"}</span>
                <span className="wa-up-inc-when">
                  {when.format(start)}
                  {end ? ` to ${when.format(end)}` : ""}
                </span>
                <span className="wa-up-inc-meta">
                  {end ? `Down ${fmtOutage(end.getTime() - start.getTime())}` : `Down for ${fmtOutage(Date.now() - start.getTime())}`}
                  {i.first_error ? ` · ${i.first_error}` : ""}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
